import { PreservingNavigate } from "@components/common/preserve-navigation/preserving-navigate";
import { TabsWithRoutes } from "@components/common/tabs/tabs-with-routes";
import { ProjectArchivingState } from "@custom-types/project-types";
import { isGroupDetails } from "@custom-types/type-guards";
import { useHasUserValidRoleGroupLevel } from "@hooks/access-control/use-has-user-valid-role-group-level";
import { ChangeGroupThumbnailButton } from "@pages/group-details/change-group-thumbnail-button";
import { GroupManagers } from "@pages/group-details/group-managers";
import { GroupProjects } from "@pages/group-details/group-projects";
import { NotFoundPage } from "@pages/not-found-page";
import { GroupTabs, MainRoutes } from "@router/route-params";
import { useAppParams } from "@router/router-helper";
import { useCompanyId } from "@context-providers/company-id-provider";
import { selectedGroupSelector } from "@store/groups/groups-selector";
import { setSelectedGroupId } from "@store/groups/groups-slice";
import { fetchGroupDetails } from "@store/groups/groups-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { bulkActionNameSelector } from "@store/table/table-selector";
import { RequiredFeatureCompanyLevelName } from "@utils/feature-control/company/company-feature-control-types";
import { OpenTabEvents } from "@utils/track-event/track-event-list";
import { useEffect, useMemo } from "react";

/**
 * Group details page
 */
export function GroupDetails(): JSX.Element {
  const { groupId, groupTabs } = useAppParams();
  const companyId = useCompanyId();
  const dispatch = useAppDispatch();

  const selectedGroup = useAppSelector(selectedGroupSelector);
  const bulkActionName = useAppSelector(bulkActionNameSelector);

  /**
   * Fetches the group details. if bulkActionName is null, it means that whether:
   * 1. the page is loaded because user opens it
   * 2. the user has just performed a bulk action on managers or projects which might affect the group details property.
   * So, in both cases, fetching would be encouraged
   * If bulkActionName is not null, it means that user has just started a bulk action and
   * group fetching is not needed as it triggers the whole group table fetching again
   */
  useEffect(() => {
    if (groupId && bulkActionName === null) {
      void dispatch(fetchGroupDetails({ companyId, groupId }));
    }
  }, [companyId, dispatch, groupId, bulkActionName]);

  useEffect(() => {
    return () => {
      dispatch(setSelectedGroupId(null));
    };
  }, [dispatch]);

  const group = useMemo(() => {
    return selectedGroup && isGroupDetails(selectedGroup)
      ? selectedGroup
      : null;
  }, [selectedGroup]);

  const { canEditGroupDetails } = useHasUserValidRoleGroupLevel({
    selectedGroup: group,
  });

  /**
   * Check if the current user has the required permissions to change the group thumbnail;
   * render the actionButton if permitted.
   */
  const actionButton = useMemo(() => {
    if (!canEditGroupDetails) {
      return undefined;
    }
    return <ChangeGroupThumbnailButton />;
  }, [canEditGroupDetails]);

  // Handle deprecated "projects" tab - redirect to "active-projects"
  if (groupTabs === GroupTabs.projects && groupId) {
    return (
      <PreservingNavigate
        to={`/${companyId}/${MainRoutes.groups}/${groupId}/${GroupTabs.activeProjects}`}
      />
    );
  }

  if (!groupTabs || !Object.values(GroupTabs).includes(groupTabs) || !groupId) {
    return <NotFoundPage />;
  }

  return (
    <TabsWithRoutes
      selectedTab={groupTabs}
      selectedProject={null}
      dataTestId="GROUP_DETAILS_CONTAINER"
      trackingEventPageName={OpenTabEvents.openGroupTab}
      requiredAttribute={group}
      tabs={[
        {
          label: "Members",
          route: GroupTabs.managers,
          content: (group) => <GroupManagers group={group} />,
          loadingContent: <GroupManagers isLoading={true} />,
          requiredFeatureCompanyLevel:
            RequiredFeatureCompanyLevelName.canViewCompanyGroups,
          actionButtons: actionButton,
        },
        {
          label: "Active projects",
          route: GroupTabs.activeProjects,
          content: (group) => (
            <GroupProjects
              companyId={companyId}
              groupId={groupId}
              group={group}
              projectState={ProjectArchivingState.active}
            />
          ),
          loadingContent: (
            <GroupProjects
              companyId={companyId}
              groupId={groupId}
              isLoading={true}
              projectState={ProjectArchivingState.active}
            />
          ),
          requiredFeatureCompanyLevel:
            RequiredFeatureCompanyLevelName.canViewCompanyGroups,
          actionButtons: actionButton,
        },
        {
          label: "Archived projects",
          route: GroupTabs.archivedProjects,
          content: (group) => (
            <GroupProjects
              companyId={companyId}
              groupId={groupId}
              group={group}
              projectState={ProjectArchivingState.archived}
            />
          ),
          loadingContent: (
            <GroupProjects
              companyId={companyId}
              groupId={groupId}
              isLoading={true}
              projectState={ProjectArchivingState.archived}
            />
          ),
          requiredFeatureCompanyLevel:
            RequiredFeatureCompanyLevelName.canViewCompanyGroups,
          actionButtons: actionButton,
        },
      ]}
    />
  );
}
